import React from 'react';

import { isBlockingOverlayOpen, isEditableTarget } from '../lib/keyboard';

/**
 * Binds Left/Right arrow keys to step through a list of receipts. The handler
 * is skipped while the user is typing in an input so it never fights with form
 * fields, and it wraps around at both ends to match the on-screen page buttons.
 *
 * @param count Number of receipts available.
 * @param setSelectedReceiptIndex Setter for the currently shown receipt index.
 * @param enabled When false, no listener is attached (e.g. modal closed).
 */
export function useReceiptArrowKeys(
  count: number,
  setSelectedReceiptIndex: React.Dispatch<React.SetStateAction<number>>,
  enabled = true,
): void {
  React.useEffect(() => {
    if (!enabled || count <= 1) {
      return;
    }

    function onKeyDown(event: KeyboardEvent) {
      if (isEditableTarget(event.target) || isBlockingOverlayOpen()) {
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setSelectedReceiptIndex((index) => (index + 1) % count);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setSelectedReceiptIndex((index) => (index - 1 + count) % count);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [count, enabled, setSelectedReceiptIndex]);
}
